import { getReferenceString } from '@medplum/core';
import { Appointment, Claim, Task, TaskOutput } from '@medplum/fhirtypes';
import {
  APPOINTMENT_TYPES_SYSTEM,
  CoverageAction,
  PRIOR_AUTH_REQUIREMENT_CODESYSTEM,
  PriorAuthRequirement,
} from './constants';
import { COVERAGE_ACTION_DOCUMENT_EXTENSION } from './extensions';
import type { PrepItem } from './payment-status';

/** Prior auth prep (BAR-3087): one Task per item, focused on the Benefit verified Claim. */

export const PRIOR_AUTH_REQUIREMENT_DISPLAY: Record<PriorAuthRequirement, string> = {
  [PriorAuthRequirement.QUESTIONNAIRE]: 'Questionnaire',
  [PriorAuthRequirement.RD_CLEARANCE]: 'RD clearance',
  [PriorAuthRequirement.NP_CLEARANCE]: 'NP clearance',
  [PriorAuthRequirement.PSYCH_EVALUATION]: 'Psych evaluation',
  [PriorAuthRequirement.PCP_CLEARANCE]: 'PCP clearance',
};

/** The actions that complete an item from the card. */
export const PREP_ITEM_BY_ACTION: Partial<Record<CoverageAction, PriorAuthRequirement>> = {
  [CoverageAction.QUESTIONNAIRE_RECEIVED]: PriorAuthRequirement.QUESTIONNAIRE,
  [CoverageAction.PSYCH_EVALUATION_RECEIVED]: PriorAuthRequirement.PSYCH_EVALUATION,
};

export function prepTaskCode(task: Task): PriorAuthRequirement | undefined {
  const code = task.code?.coding?.find((c) => c.system === PRIOR_AUTH_REQUIREMENT_CODESYSTEM)?.code;
  return Object.values(PriorAuthRequirement).find((item) => item === code);
}

/** The checklist from the newest set only: a second prior auth starts fresh. */
export function readPrepItems(tasks: Task[]): PrepItem[] {
  const newestFirst = [...tasks].sort((a, b) => (b.authoredOn ?? '').localeCompare(a.authoredOn ?? ''));
  const seen = new Set<PriorAuthRequirement>();
  return newestFirst.flatMap((task) => {
    const code = prepTaskCode(task);
    if (!code || seen.has(code)) return [];
    seen.add(code);
    const status = task.status === 'completed' || task.status === 'cancelled' ? task.status : 'requested';
    return [{ code, status, at: task.lastModified }];
  });
}

/** The open Task for one item of the set the Benefit verified Claim started. */
export function buildPrepTask(claim: Claim, item: PriorAuthRequirement): Task {
  return {
    resourceType: 'Task',
    status: 'requested',
    intent: 'order',
    code: {
      coding: [
        { system: PRIOR_AUTH_REQUIREMENT_CODESYSTEM, code: item, display: PRIOR_AUTH_REQUIREMENT_DISPLAY[item] },
      ],
    },
    description: PRIOR_AUTH_REQUIREMENT_DISPLAY[item],
    for: claim.patient,
    focus: { reference: getReferenceString(claim) },
    authoredOn: claim.created,
  };
}

/**
 * The items a clearance visit completes (BAR-3088). Each appointment type's code is the item's
 * own (`data/codesystem/appointment-types.json`).
 */
export const CLEARANCE_ITEMS: readonly PriorAuthRequirement[] = [
  PriorAuthRequirement.RD_CLEARANCE,
  PriorAuthRequirement.NP_CLEARANCE,
];

/** The item a clearance visit completes, by its appointment type. */
export function clearanceItemOf(appointment: Appointment): PriorAuthRequirement | undefined {
  const codes = (appointment.appointmentType?.coding ?? [])
    .filter((coding) => coding.system === APPOINTMENT_TYPES_SYSTEM)
    .map((coding) => coding.code);
  return CLEARANCE_ITEMS.find((item) => codes.includes(item));
}

function completed(task: Task, on: string | undefined, output: TaskOutput[]): Task {
  return { ...task, status: 'completed', lastModified: on, ...(output.length > 0 ? { output } : {}) };
}

/** Completed on the action's date, with the card's files as its output. */
export function completePrepTask(task: Task, claim: Claim): Task {
  const output: TaskOutput[] = (claim.extension ?? [])
    .filter((extension) => extension.url === COVERAGE_ACTION_DOCUMENT_EXTENSION && extension.valueReference)
    .map((extension) => ({ type: { text: 'Document' }, valueReference: extension.valueReference }));
  return completed(task, claim.created, output);
}

/** Completed when the visit ended, with the Appointment as its output. */
export function completePrepTaskByVisit(task: Task, appointment: Appointment): Task {
  return completed(task, appointment.end ?? appointment.start, [
    { type: { text: 'Appointment' }, valueReference: { reference: getReferenceString(appointment) } },
  ]);
}
